import { useEffect, useState } from 'react';
import './ProgramPages.css';
import { IconBook, IconHeart, IconLeaf, IconPalette } from './ProgramIcons';
import heroImage1 from './assets/news/news1.jpg';
import heroImage2 from './assets/news/new2.jpg';
import heroImage3 from './assets/news/news3.jpg';

function PreschoolPage({ onNavigate }) {
  const [activeHero, setActiveHero] = useState(0);
  const heroSlides = [heroImage1, heroImage2, heroImage3];

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHero((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  const programHighlights = [
    {
      icon: IconPalette,
      title: 'Play-Based Learning',
      description: 'Learning through exploration, creativity, and joyful discovery that builds curiosity and confidence.',
    },
    {
      icon: IconHeart,
      title: 'Social-Emotional Growth',
      description: 'Building friendships, sharing, empathy, and self-regulation in a caring environment.',
    },
    {
      icon: IconBook,
      title: 'Early Literacy & Numeracy',
      description: 'Foundational skills in letters, sounds, numbers, and patterns through stories and games.',
    },
    {
      icon: IconLeaf,
      title: 'Character Formation',
      description: 'Faith-based values, kindness, respect, and gratitude woven into daily routines.',
    },
  ];

  const dailySchedule = [
    { time: '07:30 - 08:00', activity: 'Arrival & Free Play', description: 'Welcoming time with friends and teachers' },
    { time: '08:00 - 08:30', activity: 'Morning Circle', description: 'Songs, prayers, and sharing time' },
    { time: '08:30 - 09:30', activity: 'Learning Centers', description: 'Hands-on activities in art, blocks, sensory, and dramatic play' },
    { time: '09:30 - 10:00', activity: 'Snack & Story Time', description: 'Healthy snack and engaging stories' },
    { time: '10:00 - 10:45', activity: 'Outdoor Play', description: 'Physical activity, exploration, and nature time' },
    { time: '10:45 - 11:30', activity: 'Guided Activities', description: 'Teacher-led lessons in literacy, math, or science' },
    { time: '11:30 - 12:00', activity: 'Closing Circle & Dismissal', description: 'Reflection, songs, and goodbye' },
  ];

  const learningAreas = [
    {
      title: 'Language & Literacy',
      description: 'Stories, songs, rhymes, and conversations that build vocabulary and listening skills.',
      image: heroImage1,
    },
    {
      title: 'Math & Logic',
      description: 'Counting, sorting, patterns, and shapes through playful hands-on activities.',
      image: heroImage2,
    },
    {
      title: 'Creative Arts',
      description: 'Painting, drawing, music, and movement that encourage self-expression.',
      image: heroImage3,
    },
    {
      title: 'Science & Discovery',
      description: 'Exploring nature, experimenting, and asking questions about the world.',
      image: heroImage1,
    },
  ];

  const parentInfo = [
    {
      title: 'Age Requirement',
      content: '3-4 years old by September 1st',
    },
    {
      title: 'Class Size',
      content: 'Maximum 15 students per class with 2 teachers',
    },
    {
      title: 'Schedule',
      content: 'Monday - Friday, 7:30 AM - 12:00 PM',
    },
    {
      title: 'What to Bring',
      content: 'Healthy snack, water bottle, extra clothes, and a smile!',
    },
  ];

  return (
    <div className="program-page preschool-page" id="preschool">
      {/* Hero Section */}
      <section className="program-hero">
        <div className="program-hero-media">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`program-hero-slide ${index === activeHero ? 'active' : ''}`}
            >
              <img src={slide} alt="Preschool" />
            </div>
          ))}
        </div>
        <div className="program-hero-overlay" />
        <div className="program-hero-content">
          <span className="program-hero-badge">Ages 3-4</span>
          <h1>Preschool Program</h1>
          <p className="program-hero-subtitle">
            Where little learners discover, play, and grow with joy and purpose
          </p>
          <div className="program-hero-stats">
            <div className="program-stat">
              <span className="stat-number">15</span>
              <span className="stat-label">Max Class Size</span>
            </div>
            <div className="program-stat">
              <span className="stat-number">2</span>
              <span className="stat-label">Teachers per Class</span>
            </div>
            <div className="program-stat">
              <span className="stat-number">4.5</span>
              <span className="stat-label">Hours Daily</span>
            </div>
          </div>
        </div>
      </section>

      {/* Program Overview */}
      <section className="program-overview">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Program Overview</span>
            <h2>A Joyful Start to Learning</h2>
            <p>
              Our preschool program creates a warm, nurturing environment where young children 
              explore, discover, and build the foundations for lifelong learning through play, 
              creativity, and meaningful relationships.
            </p>
          </div>

          <div className="program-highlights-grid">
            {programHighlights.map((item, index) => {
              const Icon = item.icon;
              return (
              <div className="highlight-card" key={index}>
                <div className="highlight-icon" aria-hidden="true">
                  <Icon />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Daily Schedule */}
      <section className="daily-schedule">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Daily Schedule</span>
            <h2>A Day in Preschool</h2>
            <p>Structured routines that balance learning, play, and rest</p>
          </div>

          <div className="schedule-timeline">
            {dailySchedule.map((item, index) => (
              <div className="schedule-item" key={index}>
                <div className="schedule-time">{item.time}</div>
                <div className="schedule-content">
                  <h4>{item.activity}</h4>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning Areas */}
      <section className="learning-areas">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">Learning Areas</span>
            <h2>What Your Child Will Explore</h2>
            <p>Developmentally appropriate activities across key learning domains</p>
          </div>

          <div className="learning-areas-grid">
            {learningAreas.map((area, index) => (
              <div className="learning-area-card" key={index}>
                <div className="learning-area-image">
                  <img src={area.image} alt={area.title} />
                </div>
                <div className="learning-area-content">
                  <h3>{area.title}</h3>
                  <p>{area.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="campus-link">
        <div className="program-container">
          <div className="campus-link-card">
            <div>
              <p className="campus-link-kicker">School Center</p>
              <h3>School center information</h3>
              <p>
                This program is part of our main campus network. Visit the central school site for
                official information and updates.
              </p>
            </div>
            <a
              className="campus-link-action"
              href="https://kinderfieldhighfield.sch.id/simprug"
              target="_blank"
              rel="noreferrer"
            >
              Open main campus site
            </a>
          </div>
        </div>
      </section>

      {/* Parent Information */}
      <section className="parent-info">
        <div className="program-container">
          <div className="section-header">
            <span className="section-kicker">For Parents</span>
            <h2>Important Information</h2>
            <p>Everything you need to know about our preschool program</p>
          </div>

          <div className="parent-info-grid">
            {parentInfo.map((item, index) => (
              <div className="info-card" key={index}>
                <h4>{item.title}</h4>
                <p>{item.content}</p>
              </div>
            ))}
          </div>

          <div className="cta-section">
            <h3>Ready to Join Our Preschool Family?</h3>
            <p>Schedule a visit to see our classrooms and meet our teachers</p>
            <div className="cta-buttons">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => onNavigate && onNavigate('register')}
              >
                Apply Now
              </button>
              <a href="/contact" className="btn btn-secondary">Schedule a Visit</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default PreschoolPage;
